import type { IngestEvent, IngestBatch } from "mindbricks-analytics-shared";
import type { LocalStore } from "./storage.js";

const QUEUE_KEY = "queue";
const MAX_BACKOFF_MS = 30_000;
const MAX_QUEUE_SIZE = 500;

interface TransportOptions {
  apiUrl: string;
  projectId: string;
  batchSize: number;
  flushIntervalMs: number;
  persistQueue: boolean;
  storage: LocalStore;
  debug?: boolean;
}

/**
 * The transport owns the in-memory queue, batching, retries, and beacon
 * delivery. It is intentionally decoupled from event construction — it
 * just sees `IngestEvent`s and ships batches.
 *
 * Network strategy:
 * - normal flush: fetch with `keepalive: true`, retry with backoff on 5xx
 * - page hide / unload: navigator.sendBeacon (sync, unsuppressed)
 *
 * Persistence:
 * - on every enqueue we write the queue to localStorage so a crash, hard
 *   reload, or sendBeacon failure doesn't lose events. On startup we drain
 *   what was left over.
 */
export class Transport {
  private opts: TransportOptions;
  private queue: IngestEvent[] = [];
  private timer: ReturnType<typeof setTimeout> | null = null;
  private isFlushing = false;
  private backoffMs = 0;
  private isOnline = typeof navigator !== "undefined" ? navigator.onLine : true;

  constructor(opts: TransportOptions) {
    this.opts = opts;
    if (opts.persistQueue) {
      const restored = opts.storage.getJson<IngestEvent[]>(QUEUE_KEY);
      if (Array.isArray(restored) && restored.length) {
        this.queue.push(...restored);
        this.scheduleFlush(0);
      }
    }
    if (typeof window !== "undefined") {
      window.addEventListener("online", () => {
        this.isOnline = true;
        this.scheduleFlush(0);
      });
      window.addEventListener("offline", () => {
        this.isOnline = false;
      });
    }
  }

  enqueue(event: IngestEvent): void {
    this.queue.push(event);
    if (this.queue.length > MAX_QUEUE_SIZE) {
      // Drop oldest to bound memory. Older events have least value.
      this.queue.splice(0, this.queue.length - MAX_QUEUE_SIZE);
    }
    this.persist();
    if (this.queue.length >= this.opts.batchSize) {
      this.scheduleFlush(0);
    } else {
      this.scheduleFlush(this.opts.flushIntervalMs);
    }
  }

  /** Force-flush via fetch (called by user code or interval). */
  async flush(): Promise<void> {
    if (this.isFlushing || this.queue.length === 0) return;
    if (!this.isOnline) return;
    this.isFlushing = true;
    try {
      while (this.queue.length > 0) {
        const batch = this.queue.splice(0, this.opts.batchSize);
        const ok = await this.send(batch, /*beacon*/ false);
        if (!ok) {
          // Put events back at the front and back off.
          this.queue.unshift(...batch);
          this.persist();
          this.backoffMs = Math.min(MAX_BACKOFF_MS, Math.max(1_000, this.backoffMs * 2 || 1_000));
          this.scheduleFlush(this.backoffMs);
          return;
        }
        this.backoffMs = 0;
        this.persist();
      }
    } finally {
      this.isFlushing = false;
    }
  }

  /**
   * Best-effort delivery during page hide / unload. Beacon is sync, has a
   * fixed payload limit (~64KB), and ignores response — so it can't retry.
   * We split the queue into beacon-sized chunks and fire each one.
   */
  flushBeacon(): void {
    if (this.queue.length === 0) return;
    if (typeof navigator === "undefined" || typeof navigator.sendBeacon !== "function") {
      // Last resort: fire-and-forget fetch with keepalive
      void this.send(this.queue.splice(0), true);
      this.persist();
      return;
    }
    while (this.queue.length > 0) {
      const chunk = this.queue.splice(0, this.opts.batchSize);
      const body = this.buildBatch(chunk);
      // text/plain content-type avoids a CORS preflight (beacon ignores
      // preflight responses, so with application/json the request would be
      // silently dropped by the browser). The API parses /v1/events/batch
      // bodies regardless of content-type.
      const blob = new Blob([JSON.stringify(body)], { type: "text/plain;charset=utf-8" });
      const sent = navigator.sendBeacon(this.url(), blob);
      if (!sent) {
        // Beacon was rejected (e.g. blob too large). Fall back to fetch keepalive.
        void this.send(chunk, true);
      }
    }
    this.persist();
  }

  size(): number {
    return this.queue.length;
  }

  private url(): string {
    return `${this.opts.apiUrl.replace(/\/$/, "")}/v1/events/batch`;
  }

  private buildBatch(events: IngestEvent[]): IngestBatch {
    return {
      projectId: this.opts.projectId,
      sentAt: new Date().toISOString(),
      events,
    };
  }

  private async send(events: IngestEvent[], keepalive: boolean): Promise<boolean> {
    if (events.length === 0) return true;
    const body = JSON.stringify(this.buildBatch(events));
    try {
      const res = await fetch(this.url(), {
        method: "POST",
        headers: { "content-type": "application/json" },
        body,
        keepalive,
        credentials: "omit",
      });
      // Treat 4xx as terminal (the batch is bad — drop it instead of looping forever).
      if (res.status >= 400 && res.status < 500) {
        if (this.opts.debug) console.warn("[analytics] dropped batch", res.status);
        return true;
      }
      return res.ok;
    } catch (e) {
      if (this.opts.debug) console.warn("[analytics] send failed", e);
      return false;
    }
  }

  private scheduleFlush(delay: number): void {
    if (this.timer) return;
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.flush();
    }, delay);
  }

  private persist(): void {
    if (!this.opts.persistQueue) return;
    if (this.queue.length === 0) this.opts.storage.remove(QUEUE_KEY);
    else this.opts.storage.setJson(QUEUE_KEY, this.queue);
  }
}
